import { TOAST_TYPE_PASS } from '../../../constants'
import { BaseScheduleShiftViewModel } from '../../../entities/BaseScheduleShift/BaseScheduleShift'
import { translate } from '../../../helpers/translations/translator'
import { AddShiftFormData, EditShiftFormData } from '../../../models'
import { setAppToast } from '../../../redux/app/appActions'
import {
    BaseScheduleShift,
    EditBaseScheduleShiftTimeFormData,
} from '../../entities/BaseScheduleShift/BaseScheduleShift'
import {
    deleteBaseScheduleShiftApiCall,
    getBaseScheduleShiftApiCall,
    patchBaseScheduleShiftApiCall,
    postBaseScheduleShiftApiCall,
} from '../../entities/BaseScheduleShift/BaseScheduleShiftService'
import {
    transformAddShiftFormDataToV3Request,
    transformBaseScheduleShiftViewModelToV3CopyRequest, transformEditBaseScheduleShiftFormDataToV3PatchRequest,
    transformEditBaseScheduleShiftTimeFormDataToPatchRequest,
} from '../../entities/BaseScheduleShift/BaseScheduleShiftTransformers'
import { isFetchResultSuccessful } from '../../entities/FetchResult'
import { setLastModifiedBaseScheduleShiftId } from '../baseSchedule/baseScheduleReducer'
import { copyBaseScheduleShiftPlanning } from '../baseScheduleShiftPlanning/baseScheduleShiftPlanningActions'
import { TypedDispatch } from '../store'
import {
    setBaseScheduleShift,
    setDeletedBaseScheduleShiftId,
    setError,
    setIsDeleteBaseScheduleShiftSuccessful,
    setIsEditBaseScheduleShiftSuccessful,
    setIsLoading,
    setIsSuccessful,
} from './baseScheduleShiftReducer'

export const fetchBaseScheduleShift = (id: string) => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch(setIsLoading(true))
    dispatch(setBaseScheduleShift(undefined))
    dispatch(setError(''))

    try {
        const response = await getBaseScheduleShiftApiCall(id)

        if (! isFetchResultSuccessful(response)) {
            dispatch(setError(response.error))

            return
        }

        dispatch(setBaseScheduleShift(response.data))
    } catch (error) {
        console.error('[fetchBaseScheduleShift]', error)
    } finally {
        dispatch(setIsLoading(false))
    }
}

export const createBaseScheduleShift = (
    formData: AddShiftFormData,
    weekNumber = 1,
    baseScheduleId: string,
) => async (
    dispatch: TypedDispatch,
): Promise<void> => {
    dispatch(setIsLoading(true))
    dispatch(setError(''))

    try {
        const request = transformAddShiftFormDataToV3Request(formData, baseScheduleId, weekNumber)
        const response = await postBaseScheduleShiftApiCall(request)

        if (! isFetchResultSuccessful(response)) {
            dispatch(setError(response.error))

            return
        }

        dispatch(setAppToast(translate('pages.shifts.addBaseScheduleShiftSuccess'), TOAST_TYPE_PASS))
        dispatch(setLastModifiedBaseScheduleShiftId(response.data.id))
        dispatch(setBaseScheduleShift(response.data))
        dispatch(setIsSuccessful(true))
    } catch (error) {
        console.error('[createBaseScheduleShift]', error)
    } finally {
        dispatch(setIsLoading(false))
    }
}

export const editBaseScheduleShift = (
    baseScheduleShiftId: string,
    formData: EditShiftFormData,
) => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch(setIsLoading(true))
    dispatch(setError(''))

    try {
        const request = transformEditBaseScheduleShiftFormDataToV3PatchRequest(formData)

        const response = await patchBaseScheduleShiftApiCall(baseScheduleShiftId, request)

        if (! isFetchResultSuccessful(response)) {
            dispatch(setError(response.error))

            return
        }

        dispatch(setAppToast(translate('pages.shifts.editShiftSuccess'), TOAST_TYPE_PASS))
        dispatch(setLastModifiedBaseScheduleShiftId(baseScheduleShiftId))
        dispatch(setIsSuccessful(true))
    } catch (error) {
        console.error('[editBaseScheduleShift]', error)
    } finally {
        dispatch(setIsLoading(false))
    }
}

export const editBaseScheduleShiftTime = (
    baseScheduleShiftId: string,
    formData: EditBaseScheduleShiftTimeFormData,
) => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch(setIsLoading(true))
    dispatch(setIsEditBaseScheduleShiftSuccessful(false))
    dispatch(setError(''))

    try {
        const request = transformEditBaseScheduleShiftTimeFormDataToPatchRequest(formData)

        const response = await patchBaseScheduleShiftApiCall(baseScheduleShiftId, request)

        if (! isFetchResultSuccessful(response)) {
            dispatch(setError(response.error))

            return
        }

        dispatch(setAppToast(translate('pages.shifts.editShiftSuccess'), TOAST_TYPE_PASS))
        dispatch(setBaseScheduleShift(response.data))
        dispatch(setIsEditBaseScheduleShiftSuccessful(true))
    } catch (error) {
        console.error('[editBaseScheduleShiftTime]', error)
    } finally {
        dispatch(setIsLoading(false))
    }
}

export const deleteBaseScheduleShift = (
    baseScheduleShiftId: string,
) => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch(setIsLoading(true))
    dispatch(setIsDeleteBaseScheduleShiftSuccessful(false))
    dispatch(setError(''))

    try {
        const response = await deleteBaseScheduleShiftApiCall(baseScheduleShiftId)

        if (! isFetchResultSuccessful(response)) {
            dispatch(setError(response.error))

            return
        }

        dispatch(setAppToast(translate('pages.shifts.deleteShiftSuccess'), TOAST_TYPE_PASS))
        dispatch(setDeletedBaseScheduleShiftId(baseScheduleShiftId))
        dispatch(setIsDeleteBaseScheduleShiftSuccessful(true))
    } catch (error) {
        console.error('[deleteBaseScheduleShift]', error)
    } finally {
        dispatch(setIsLoading(false))
    }
}

export const copyBaseScheduleShift = (
    baseScheduleShift: BaseScheduleShiftViewModel,
) => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch(setIsLoading(true))
    dispatch(setError(''))

    try {
        const request = transformBaseScheduleShiftViewModelToV3CopyRequest(baseScheduleShift)
        const response = await postBaseScheduleShiftApiCall(request)

        if (! isFetchResultSuccessful(response)) {
            dispatch(setError(response.error))

            return
        }

        if (baseScheduleShift.shiftPlannings.length > 0) {
            try {
                await Promise.all(
                    baseScheduleShift.shiftPlannings.map(shiftPlanning => dispatch(
                        copyBaseScheduleShiftPlanning(shiftPlanning, response.data.id),
                    )),
                )
            } catch (error) {
                console.error('[copyBaseScheduleShift]', error)
                await dispatch(deleteBaseScheduleShift(response.data.id))
            }
        }

        dispatch(setAppToast(translate('pages.shifts.copyShiftSuccess'), TOAST_TYPE_PASS))
        dispatch(setLastModifiedBaseScheduleShiftId(response.data.id))
        dispatch(setIsSuccessful(true))
    } catch (error) {
        console.error('[copyBaseScheduleShift]', error)
    } finally {
        dispatch(setIsLoading(false))
    }
}
